import { useState } from "react";
import { Loader2, Send, User, Mail, Phone, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const PROJEKTTYPEN = ["Einfamilienhaus", "Mehrfamilienhaus", "Gewerbe", "Sanierung", "Anbau", "Andere"];

export default function ContactForm({ source = "kontaktformular", compact = false }) {
  const [form, setForm] = useState({
    name: "", email: "", telefon: "", projekttyp: "", nachricht: "", rueckruf: false, consent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Bitte Datenschutz akzeptieren.");
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, quelle: source });
      toast.success("Vielen Dank! Wir melden uns binnen 24h.");
      setForm({ name: "", email: "", telefon: "", projekttyp: "", nachricht: "", rueckruf: false, consent: false });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className={`bg-white border border-slate-200 p-8 md:p-10 rounded-sm ${compact ? "" : "shadow-xl shadow-[#0C2749]/5"}`}
      data-testid="contact-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field icon={User} placeholder="Ihr Name *" value={form.name} onChange={(v) => update("name", v)} testid="contact-name" required />
        <Field icon={Mail} placeholder="E-Mail *" type="email" value={form.email} onChange={(v) => update("email", v)} testid="contact-email" required />
        <Field icon={Phone} placeholder="Telefon" value={form.telefon} onChange={(v) => update("telefon", v)} testid="contact-phone" />
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <select
            value={form.projekttyp}
            onChange={(e) => update("projekttyp", e.target.value)}
            className="w-full pl-10 pr-3 py-3 border border-slate-200 focus:border-[#2873B8] focus:outline-none rounded-sm bg-white text-[#0C2749] text-sm"
            data-testid="contact-projekttyp"
          >
            <option value="">Projekttyp wählen</option>
            {PROJEKTTYPEN.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <textarea
        placeholder="Beschreiben Sie kurz Ihr Projekt …"
        value={form.nachricht}
        onChange={(e) => update("nachricht", e.target.value)}
        rows={5}
        className="mt-4 w-full p-4 border border-slate-200 focus:border-[#2873B8] focus:outline-none rounded-sm bg-white text-[#0C2749] text-sm resize-none"
        data-testid="contact-message"
      />
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4 text-sm">
        <label className="inline-flex items-center gap-2 text-slate-700">
          <input type="checkbox" checked={form.rueckruf} onChange={(e) => update("rueckruf", e.target.checked)} className="accent-[#2873B8]" data-testid="contact-callback" />
          Bitte rufen Sie mich zurück
        </label>
        <label className="inline-flex items-start gap-2 text-slate-600 text-xs flex-1">
          <input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} className="mt-0.5 accent-[#2873B8]" data-testid="contact-consent" />
          Ich akzeptiere die Datenschutzerklärung. Meine Daten werden ausschließlich zur Bearbeitung meiner Anfrage verwendet.
        </label>
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="btn-shimmer mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#0C2749] hover:bg-[#2873B8] text-white px-8 py-4 rounded-sm font-medium transition-colors disabled:opacity-60"
        data-testid="contact-submit"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Anfrage senden
      </button>
    </form>
  );
}

function Field({ icon: Icon, placeholder, type = "text", value, onChange, testid, required }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full pl-10 pr-3 py-3 border border-slate-200 focus:border-[#2873B8] focus:outline-none rounded-sm bg-white text-[#0C2749] text-sm"
        data-testid={testid}
      />
    </div>
  );
}
